import { useEffect, useRef } from "react";

const SCAN_GAP_MS = 300;

const isTypingInOtherField = (target, scanInput) => {
  if (!target) return false;
  // Let the scan field handle keystrokes directly (manual entry + focused scanner).
  if (scanInput && target === scanInput) return true;
  const tag = target.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag === "INPUT") {
    if (target.readOnly) return false;
    const type = (target.type || "text").toLowerCase();
    if (type === "checkbox" || type === "radio" || type === "button" || type === "submit") {
      return false;
    }
    return true;
  }
  if (target.isContentEditable) return true;
  return false;
};

/**
 * Captures scanner input at document level (capture phase) so rapid bulk dumps
 * never collide in a React-controlled or default input value.
 */
const useScannerBuffer = ({ enabled, inputRef, onScan }) => {
  const bufferRef = useRef("");
  const lastKeyTimeRef = useRef(0);
  const onScanRef = useRef(onScan);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (e) => {
      if (isTypingInOtherField(e.target, inputRef.current)) return;

      const now = Date.now();
      if (now - lastKeyTimeRef.current > SCAN_GAP_MS && bufferRef.current) {
        bufferRef.current = "";
        if (inputRef.current) inputRef.current.value = "";
      }
      lastKeyTimeRef.current = now;

      if (e.key === "Enter" || (e.key === "Tab" && bufferRef.current)) {
        e.preventDefault();
        const raw = bufferRef.current.trim();
        bufferRef.current = "";
        if (inputRef.current) inputRef.current.value = "";
        if (raw) onScanRef.current(raw);
        return;
      }

      if (e.key.length === 1 && /^\d$/.test(e.key)) {
        e.preventDefault();
        bufferRef.current += e.key;
        if (inputRef.current) {
          inputRef.current.value = bufferRef.current;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [enabled, inputRef]);
};

export default useScannerBuffer;
